import { Box, MenuItem, Select, Typography } from '@mui/material';
import { useIntl, FormattedMessage } from 'react-intl';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import StructureSwatch from '~/components/Structures/StructureSwatch';
import { dicomColorToRgbString } from '~/utils/color';
import { mutedNoteSx, warningSx } from './constraintStyles';

interface InsideConstraintSectionProps {
    /**
     * The modifier is off. Its settings show but are held, so nothing can
     * be set that does not apply: turn the modifier on to change them.
     */
    disabled: boolean;
    /** roiNumber of the selected bounding structure, or null. */
    roiNumber: number | null;
    /** Structures selectable as the bounding structure. */
    candidates: readonly RoiReadModel[];
    /**
     * Why the enabled constraint clips nothing, as a message id. Undefined
     * while it applies.
     */
    inactiveReasonId?: string;
    /**
     * Erase mode is active: the selection is persisted but not applied to the
     * stroke. The controls stay interactive so the user can configure them for
     * the next draw; a note explains they are dormant right now.
     */
    erasing: boolean;
    onSelect: (roiNumber: number) => void;
}

/**
 * The settings of the Inside (bounding) constraint, which clips drawing to
 * the interior of one structure: the structure to clip to. It is presentational:
 * all state lives in the parent orchestrator.
 */
export default function InsideConstraintSection({
    disabled,
    roiNumber,
    candidates,
    inactiveReasonId,
    erasing,
    onSelect,
}: InsideConstraintSectionProps) {
    const intl = useIntl();
    const selectedValue =
        roiNumber !== null && candidates.some((r) => r.roiNumber === roiNumber)
            ? roiNumber
            : '';

    return (
        <>
            {erasing && (
                <Typography sx={mutedNoteSx}>
                    <FormattedMessage id="constraintNotAppliedWhileErasing" />
                </Typography>
            )}
            <Select<number | ''>
                value={selectedValue}
                onChange={(e) => onSelect(Number(e.target.value))}
                disabled={disabled || candidates.length === 0}
                displayEmpty
                size="small"
                fullWidth
                renderValue={(value) => {
                    if (value === '') {
                        return intl.formatMessage({
                            id: 'boundingConstraintPlaceholder',
                        });
                    }
                    const roi = candidates.find((r) => r.roiNumber === value);
                    if (!roi) {
                        return '';
                    }
                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <StructureSwatch
                                color={dicomColorToRgbString(roi.color)}
                            />
                            <span>{roi.name}</span>
                        </Box>
                    );
                }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 240 } } }}
                sx={{ fontSize: 12 }}
            >
                {candidates.map((roi) => (
                    <MenuItem
                        key={roi.roiNumber}
                        value={roi.roiNumber}
                        sx={{
                            fontSize: 12,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <StructureSwatch
                            color={dicomColorToRgbString(roi.color)}
                        />
                        {roi.name}
                    </MenuItem>
                ))}
            </Select>
            {inactiveReasonId !== undefined && (
                <Typography sx={warningSx}>
                    <FormattedMessage id={inactiveReasonId} />
                </Typography>
            )}
        </>
    );
}
